<script setup lang="ts">
/**
 * Bullet chart: actual against plan on one bar. The fill is the actual value,
 * the upright mark the target; above the bar the actual figure and how much of
 * the target it reaches, below it the scale. The fill's `tone` says how the
 * actual stands (`success` met, `warning` behind) — the caller decides.
 *
 * Without `max` the scale runs to 15 % past the larger of actual and target,
 * so the target mark never sits on the edge.
 */
import { computed, inject } from 'vue';
import { i18nInjectionKey } from '../internal';
import { formatCompactNumber } from './axis';
import type { NxChartTone } from './series';

const props = withDefaults(
  defineProps<{
    value: number | null | undefined;
    target: number | null | undefined;
    max?: number | null;
    tone?: NxChartTone;
    /** Caption of the actual value. */
    valueLabel?: string;
    /** Caption of the target. */
    targetLabel?: string;
    /** Text of the share of the target; `{value}` is replaced by the percentage. */
    shareLabel?: string;
    ariaLabel?: string;
    formatValue?: (value: number) => string;
    locale?: string;
  }>(),
  {
    max: null,
    tone: 'brand',
    valueLabel: 'Ist',
    targetLabel: 'Plan',
    shareLabel: '{value} vom Plan',
    ariaLabel: undefined,
    formatValue: undefined,
    locale: undefined,
  }
);

const i18n = inject(i18nInjectionKey, null);
const locale = computed(() => props.locale ?? i18n?.locale?.value);
const fmt = (v: number) =>
  (props.formatValue ?? ((n: number) => formatCompactNumber(n, locale.value)))(v);

const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

const scaleMax = computed(() => {
  if (finite(props.max) && props.max > 0) return props.max;
  const top = Math.max(
    finite(props.value) ? props.value : 0,
    finite(props.target) ? props.target : 0
  );
  return top > 0 ? top * 1.15 : 1;
});
const pct = (v: number) => Math.max(0, Math.min(100, (v / scaleMax.value) * 100));
const valuePct = computed(() => (finite(props.value) ? pct(props.value) : 0));
const targetPct = computed(() => (finite(props.target) ? pct(props.target) : null));

const shareText = computed(() => {
  if (!finite(props.value) || !finite(props.target) || props.target === 0) return '';
  const share = new Intl.NumberFormat(locale.value, {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(props.value / props.target);
  return props.shareLabel.replace('{value}', share);
});
const color = computed(() => `var(--nxt-${props.tone}-bg)`);
const valueText = computed(() => (finite(props.value) ? fmt(props.value) : '–'));
const targetText = computed(() => (finite(props.target) ? fmt(props.target) : '–'));
</script>

<template>
  <div
    class="nx-bullet"
    role="meter"
    :aria-label="props.ariaLabel ?? props.valueLabel"
    :aria-valuenow="finite(props.value) ? props.value : undefined"
    :aria-valuemin="0"
    :aria-valuemax="scaleMax"
    :aria-valuetext="`${props.valueLabel} ${valueText}, ${props.targetLabel} ${targetText}`"
  >
    <div class="nx-bullet__head" aria-hidden="true">
      <span class="nx-bullet__figure">{{ valueText }}</span>
      <span v-if="shareText" class="nx-bullet__share" :class="`tone-${props.tone}`">
        {{ shareText }}
      </span>
    </div>
    <div class="nx-bullet__track" aria-hidden="true">
      <div
        class="nx-bullet__fill"
        :style="{ width: `${valuePct}%`, background: color }"
        data-testid="nx-bullet-value"
      />
      <div
        v-if="targetPct !== null"
        class="nx-bullet__mark"
        :style="{ left: `${targetPct}%` }"
        data-testid="nx-bullet-target"
      />
    </div>
    <div class="nx-bullet__legend" aria-hidden="true">
      <span class="nx-bullet__key">
        <span class="nx-bullet__swatch" :style="{ background: color }" />
        {{ props.valueLabel }}
        <strong>{{ valueText }}</strong>
      </span>
      <span class="nx-bullet__key">
        <span class="nx-bullet__swatch is-mark" />
        {{ props.targetLabel }}
        <strong>{{ targetText }}</strong>
      </span>
    </div>
  </div>
</template>

<style scoped>
.nx-bullet {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  width: 100%;
  min-width: 0;
}
.nx-bullet__head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--nxt-space-2xs) var(--nxt-space-sm);
}
.nx-bullet__figure {
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-2xl);
  font-weight: var(--nxt-font-weight-semibold);
  letter-spacing: -0.02em;
  line-height: var(--nxt-line-height-tight);
}
.nx-bullet__share {
  padding: 0 var(--nxt-space-xs);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-neutral-subtle-bg);
  color: var(--nxt-neutral-subtle-fg);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  line-height: 1.6;
}
.nx-bullet__share.tone-success {
  background: var(--nxt-success-subtle-bg);
  color: var(--nxt-success-subtle-fg);
}
.nx-bullet__share.tone-warning {
  background: var(--nxt-warning-subtle-bg);
  color: var(--nxt-warning-subtle-fg);
}
.nx-bullet__share.tone-danger {
  background: var(--nxt-danger-subtle-bg);
  color: var(--nxt-danger-subtle-fg);
}
.nx-bullet__track {
  position: relative;
  height: calc(var(--nxt-size-unit) * 1);
  margin-block: var(--nxt-space-2xs);
  border-radius: var(--nxt-radius-sm);
  background: var(--nxt-chart-track);
}
.nx-bullet__fill {
  position: absolute;
  inset-block: 25%;
  inset-inline-start: 0;
  max-width: 100%;
  border-radius: 0 calc(var(--nxt-hairline) * 4) calc(var(--nxt-hairline) * 4) 0;
  transform-origin: left center;
  animation: nx-bullet-grow var(--nxt-motion-slow) var(--nxt-ease-out) both;
}
.nx-bullet__mark {
  position: absolute;
  inset-block: -25%;
  width: calc(var(--nxt-hairline) * 3);
  margin-inline-start: calc(var(--nxt-hairline) * -1.5);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-fg);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-surface-bg);
}
.nx-bullet__legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: var(--nxt-space-2xs) var(--nxt-space-md);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
}
.nx-bullet__key {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-xs);
}
.nx-bullet__key strong {
  color: var(--nxt-fg-subtle);
  font-weight: var(--nxt-font-weight-semibold);
  font-variant-numeric: tabular-nums;
}
.nx-bullet__swatch {
  width: var(--nxt-dot-size-md);
  height: var(--nxt-dot-size-md);
  border-radius: calc(var(--nxt-hairline) * 2);
}
.nx-bullet__swatch.is-mark {
  width: calc(var(--nxt-hairline) * 3);
  height: calc(var(--nxt-dot-size-md) * 1.5);
  background: var(--nxt-fg);
}
@keyframes nx-bullet-grow {
  from {
    transform: scaleX(0);
  }
}
@media (prefers-reduced-motion: reduce) {
  .nx-bullet__fill {
    animation: none;
  }
}
</style>
